import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { getDocumentsDropdown } from "../../../../redux/document/action";
import { getReasonsDropdown } from "../../../../redux/reason/action.js";
// import { saveIndexedData } from "../../DataEntry/indexedDB";

// Validation schema
const additionalInfoSchema = Yup.object().shape({
  status: Yup.string().required("Required"),
  reason: Yup.array()
    .of(Yup.number())
    .when("status", {
      is: "INCOMPLETE",
      then: (schema) => schema.min(1, "Reason Required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  document_classification: Yup.string(),
});

// Default form values
const defaultValues = {
  status: "",
  reason: [],
  document_classification: "",
  is_orphaned_check: false,
  is_missing_check_image: false,
  is_forced_balancing: false,
  is_payer_black_listing: false,
  is_payer_excluding_indexing: false,
};

export const useAdditionalInfo = ({ 
  jobData, 
  eobFormGetValues, 
  eobFormSetValues, 
  setIsDragEnabled,
  eobFormErrors,
  eobSetError,
  triggerEobValidation
}) => {
  // Form setup
  const form = useForm({
    defaultValues,
    resolver: yupResolver(additionalInfoSchema),
    mode: "onBlur"
  });

  const { trigger,register, handleSubmit, setValue, control, watch, reset, formState: { errors }, getValues } = form;

  // Additional Info State (integrated from useAdditionalInfoState)
  const [isAdditionalInfoOptions, setIsAdditionalInfoOptions] = useState({
    status: jobData?.client_level_customization?.is_additional_info_status,
    reason: jobData?.client_level_customization?.is_additional_info_reason,
    document_classification: jobData?.client_level_customization?.is_additional_info_document_classification,
    extra_info: jobData?.client_level_customization?.is_additional_info_extra_info,
  });

  const [isIncomplete, setIsIncomplete] = useState(false);
  const [additionalModal, setAdditionalModal] = useState(false);
  const aditionalInfoRef = useRef(null);

  // Component state
  const [reasonData, setReasonData] = useState([]);
  const [documentData, setDocumentData] = useState([]);
  const [filterState, setFilterState] = useState({});

  // Form values
  const values = watch();
  const formValues = eobFormGetValues();

  // Additional Info Handlers (integrated from useAdditionalInfoState)
  const handleAdditionalCloseButton = () => {
    if (Object.keys(eobFormErrors || {}).length !== 0) {
      eobSetError("qdx_reason", { type: "manual", message: "Required" });
    }
  };

  const handleOpenAdditionalInfo = () => {
    if (!additionalModal) {
      setAdditionalModal(true);
    }
  };

  // Update isAdditionalInfoOptions when jobData changes
  useEffect(() => {
    setIsAdditionalInfoOptions({
      status: jobData?.client_level_customization?.is_additional_info_status,
      reason: jobData?.client_level_customization?.is_additional_info_reason,
      document_classification: jobData?.client_level_customization?.is_additional_info_document_classification,
      extra_info: jobData?.client_level_customization?.is_additional_info_extra_info,
    });
  }, [jobData?.client_level_customization]);

//   // Load dropdown data
  useEffect(() => {
    if (jobData?.client_id) {
      // Load reasons dropdown
      getReasonsDropdown(jobData.client_id)((response) => {
        if (response?.status && response?.status_code === 200) {
          setReasonData(response?.data?.status_data);
        }
      });

      // Load documents dropdown
      getDocumentsDropdown(jobData.client_id)((response) => {
        if (response?.status && response?.status_code === 200) {
          setDocumentData(response?.data?.results);
        }
      });
    }
  }, [jobData?.client_id]);

//   // Reset form when modal opens
    useEffect(() => {
    if (additionalModal) {
      const currentFormValues = eobFormGetValues(); // Get fresh values each time
      
      reset({
        status: currentFormValues.qdx_status || "",
        reason: currentFormValues.qdx_reason || [],
        document_classification: currentFormValues.qdx_classification || "",
        is_orphaned_check: currentFormValues.is_orphaned_check || false,
        is_missing_check_image: currentFormValues.is_missing_check_image || false,
        is_forced_balancing: currentFormValues.is_forced_balancing || false,
        is_payer_black_listing: currentFormValues.is_payer_black_listing || false,
        is_payer_excluding_indexing: currentFormValues.is_payer_excluding_indexing || false,
      });
      trigger()
      setIsDragEnabled(false);
    }
  }, [additionalModal, reset,trigger, setIsDragEnabled, eobFormGetValues]);

  // Check number generation logic
  const generateCheckNumber = (status, currentCheckNumber) => {
    if (formValues?.transaction_type === 'CORRESPONDENCE_835') {
      const check = currentCheckNumber.trim();
      const isIncompleteCheckNumber = jobData?.client_level_customization?.is_incomplete_check_number;
      const isGeneratedCheckNumber = jobData?.client_level_customization?.is_generated_check_number;
      const isSubJob = jobData?.is_sub_job; 
      const isFirstJob = jobData?.is_first_sub_job; 

      if (isIncompleteCheckNumber && isGeneratedCheckNumber && (!isSubJob || isFirstJob)) {
        if (formValues.qdx_status !== status) {
          if (status === 'INCOMPLETE' && (check === '' || check.slice(0, 2).toUpperCase() === 'LT')) {
            return "RX" + jobData?.grid_data?.timezone;
          } else if (status === 'COMPLETE' && (check === '' || check.slice(0, 2).toUpperCase() === 'RX')) {
            return "LT" + jobData?.grid_data?.timezone;
          }
        } else if (check === '') {
          return status === 'INCOMPLETE' ? "RX" + jobData?.grid_data?.timezone : "LT" + jobData?.grid_data?.timezone;
        }
      }
    }
    return currentCheckNumber;
  };

  // Form submission handler
  const onSubmit = (values) => {
    setAdditionalModal(false);
    
    const check_number = generateCheckNumber(values.status, formValues.check_number);

    // Update form values
    eobFormSetValues("qdx_status", values.status);
    eobFormSetValues("qdx_reason", values.reason);
    eobFormSetValues("qdx_classification", values.document_classification);
    eobFormSetValues("is_orphaned_check", values.is_orphaned_check);
    eobFormSetValues("is_missing_check_image", values.is_missing_check_image);
    eobFormSetValues("is_forced_balancing", values.is_forced_balancing);
    eobFormSetValues("is_payer_black_listing", values.is_payer_black_listing);
    eobFormSetValues("is_payer_excluding_indexing", values.is_payer_excluding_indexing);
    eobFormSetValues("check_number", check_number);


    
  
  // Get current grid_data before updates

  
  // Update form values
  const updates = {
    qdx_status: values.status,
    qdx_reason: values.reason,
    qdx_classification: values.document_classification,
    is_orphaned_check: values.is_orphaned_check,
    is_missing_check_image: values.is_missing_check_image,
    is_forced_balancing: values.is_forced_balancing,
    is_payer_black_listing: values.is_payer_black_listing,
    is_payer_excluding_indexing: values.is_payer_excluding_indexing,
    check_number: check_number
  };
  
  // Apply updates and track changes
  Object.entries(updates).forEach(([field, newValue]) => {
    const oldValue = jobData?.grid_data?.[field];
    
    // Set the new value
    eobFormSetValues(field, newValue);
    
    // Update edited_data if value changed
    if (oldValue != newValue) {
      if (!(eobFormGetValues()?.edited_data)) {
        eobFormSetValues("edited_data", {});
      }
      eobFormSetValues(`edited_data.${field}`, {
        old_val: oldValue,
        error: true
      });
    }else{
      eobFormSetValues(`edited_data.${field}`, {
      })
    }
  })


    
    // saveIndexedData("grid_data_cache", eobFormGetValues());
    
    setIsDragEnabled(true);
    document.getElementById('additional-btn')?.focus();
    triggerEobValidation()

  };

  // Handle modal close
  const handleModalClose = () => {
    setAdditionalModal(false);
    setIsDragEnabled(true);
    handleAdditionalCloseButton();
  };

  // Handle status change
  const handleStatusChange = (status) => {
    setValue("status", status);
    setIsIncomplete(status === 'INCOMPLETE');
  };
  return {
    // Form methods
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    reset,
    errors,
    getValues,
    trigger,
    
    // State
    reasonData,
    documentData,
    filterState,
    values,
    formValues,
    
    // Additional Info State (from useAdditionalInfoState)
    isAdditionalInfoOptions,
    setIsAdditionalInfoOptions,
    isIncomplete,
    setIsIncomplete,
    aditionalInfoRef,
    additionalModal,
    setAdditionalModal,
    
    // Handlers
    onSubmit,
    handleModalClose,
    handleStatusChange,
    handleAdditionalCloseButton,
    handleOpenAdditionalInfo,
    
    // Setters for external use if needed
    setFilterState,
  };
};